import React, { useEffect, useMemo, useState, useRef } from 'react';
import RegisterFormInput from 'components/RegisterFormInput/RegisterFormInput';
import { useForm, Controller } from 'react-hook-form';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames/bind';
import FormInput from 'components/FormInput/FormInput';
import FormSelect from 'components/FormSelect/FormSelect';
import { useAppSelector } from 'app/hooks';
import { selectCurrentMarket } from 'features/core/selectors';
import { Market } from 'common/consts/markets';
import { format } from 'date-fns';
import styles from './DebtSection.module.scss';
import ReferenceNumberPopup from 'templates/DirectPaymentPageData/components/ReferenceNumberPopup/ReferenceNumberPopup';
import ValidationMessage from '../ValidationMessage/ValidationMessage';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import TagManager from 'react-gtm-module';
import { formProgression } from 'common/helpers/Gtm';
import { RegexNumber } from 'common/helpers/RegexNumber';
import useOnClickOutside from 'common/hooks/useOnClickOutside';
import { validateDOBPayload } from 'common/helpers/DateofBirthValidation';
import CustomerNotAuthorized from '../CustomerNotAuthorized/CustomerNotAuthorized';

type Props = {
  Model?: any;
  PageData?: any;
  LayoutModel?: any;
  onVerifyIdentity: (data: any) => void;
};

/**
 * Helper function to fill select with numeric values
 * @param from
 * @param to
 * @param defaultValue
 * @returns
 */
export const getNumericOptionsArray = (
  from: number,
  to: number,
  defaultValue?: string,
  isDescending?: boolean,
) => {
  const options: any[] = [];
  for (let i = from; i <= to; i++) {
    if (i <= 9) {
      options.push('0' + i);
    } else {
      options.push(i);
    }
  }
  if (isDescending) {
    options.reverse();
  }
  if (defaultValue) {
    options.unshift(defaultValue);
  }
  return options;
};

const DebtSection = ({
  Model,
  PageData,
  LayoutModel,
  onVerifyIdentity,
}: Props) => {
  const [showHiddenInput, setShowHiddenInput] = useState<Boolean>(false);
  const isUserAuthenticated = useMemo(() => {
    return Model?.IsUserAuthenticated;
  }, [Model]);
  const [showPopup, setShowPopup] = useState(false);
  const [showValidationPopup, setShowValidationPopup] = useState(
    Model?.FormModel?.VerifyIdentityFormModel?.ProfileHasError,
  );
  const [showCustomerNotAuthorizedPopup, setShowCustomerNotAuthorizedPopup] = useState(
    Model?.FormModel?.VerifyIdentityFormModel?.CustomerNotAuthorizedError,
  );
  const [isFiscalCodeEnabled, setIsFiscalCodeEnabled] = useState(
    Model?.FormModel?.VerifyIdentityFormModel?.IsFiscalCodeEnabled,
  );
  const { executeRecaptcha } = useGoogleReCaptcha(); // Recaptcha v3
  const [isNameEnabled, setIsNameEnabled] = useState(
    Model?.FormModel?.VerifyIdentityFormModel?.IsNameEnabled,
  );
  const [width, setWidth] = useState<number>(window.innerWidth);
  const [showDobValidation, setShowDobValidation] = useState<Boolean>(
    false
  );
  const [hasDOBError, setHasDOBError] = useState<Boolean>(false);

  const [dateOfBirthHasError, setDateOfBirthHasError] = useState<Boolean>(
    Model?.FormModel?.VerifyIdentityFormModel
      ?.DateOfBirthHasError
  );

  const handleDobMobileChange = (e: any, callback: Function) => {
    DateOfBirthMobileUpdate(e);
    callback(e.target.value);
  };

  const [registrationAttemptCount, setRegistrationAttemptCount] =
    useState<number>(1);

  useEffect(() => {
    if (
      !isUserAuthenticated &&
      Model?.FormModel?.VerifyIdentityFormModel?.ProfileHasError &&
      showValidationPopup
    ) {
      DetailsIncorrectTracking();
    }
  }, [
    Model?.FormModel?.VerifyIdentityFormModel?.ProfileHasError,
    showValidationPopup,
  ]);

  const DetailsIncorrectTracking = () => {
    var tagManagerArgs = {
      dataLayer: {
        event: 'form_error',
        checkout_name: 'Single Payment - Logged Out',
        error_message: 'Details not correct',
        failure_number: registrationAttemptCount,
      },
    };

    TagManager.dataLayer(tagManagerArgs);
  };

  function DateOfBirthMobileUpdate(e: any) {
    var mobileDateValue: Date;

    if (e != null || e != undefined) {
      var datemob = e.target.value;
      mobileDateValue = new Date(datemob);
    } else {
      mobileDateValue = new Date(
        getValues('VerifyIdentityFormModel.DateOfBirth'),
      );
    }
    if (!isNaN(mobileDateValue.getTime())) {
      let mobileDay = parseInt(mobileDateValue.getDate().toString());
      var mobileMonth = parseInt(mobileDateValue.getMonth().toString()) + 1;
      var mobileYear = parseInt(mobileDateValue.getFullYear().toString());
      setValue('VerifyIdentityFormModel.DateOfBirthDay', mobileDay);
      setValue('VerifyIdentityFormModel.DateOfBirthMonth', mobileMonth);
      setValue('VerifyIdentityFormModel.DateOfBirthYear', mobileYear);
    }
  }

  const { control, register, handleSubmit, setValue, getValues, watch } = useForm({
    defaultValues: {
      VerifyIdentityFormModel: {
        DebtReference: Model?.FormModel?.VerifyIdentityFormModel?.DebtReference,
        ExternalPaymentAmount:
          Model?.FormModel?.VerifyIdentityFormModel?.ExternalPaymentAmount,
        DateOfBirthDay:
          Model?.FormModel?.VerifyIdentityFormModel?.DateOfBirthDay || '',
        DateOfBirthMonth:
          Model?.FormModel?.VerifyIdentityFormModel?.DateOfBirthMonth || '',
        DateOfBirthYear:
          Model?.FormModel?.VerifyIdentityFormModel?.DateOfBirthYear || '',
        DateOfBirth:
          Model?.FormModel?.VerifyIdentityFormModel?.DateOfBirth || '',
        PersonalIdentifier:
          Model?.FormModel?.VerifyIdentityFormModel?.PersonalIdentifier,
        IsFiscalCodeEnabled:
          Model?.FormModel?.VerifyIdentityFormModel?.IsFiscalCodeEnabled,
        IsNameEnabled: Model?.FormModel?.VerifyIdentityFormModel?.IsNameEnabled,
        FiscalCode: isFiscalCodeEnabled
          ? Model?.FormModel?.VerifyIdentityFormModel?.FiscalCode
          : '',
        Name: isNameEnabled
          ? Model?.FormModel?.VerifyIdentityFormModel?.Name
          : '',
      },
      IsCalledFormExternalLink: Model?.IsCalledFromExternalLink,
    },
  });
  const watchAllFields = watch();
  const currentMarket = useAppSelector(selectCurrentMarket);

  const daysOptions = useMemo(() => {
    return getNumericOptionsArray(1, 31, PageData?.DayPlaceholder);
  }, [PageData]);

  const monthOptions = useMemo(() => {
    return getNumericOptionsArray(1, 12, PageData?.MonthPlaceholder);
  }, [PageData]);

  useEffect(() => {
    setShowValidationPopup(
      Model?.FormModel?.VerifyIdentityFormModel?.ProfileHasError,
    );
    setShowCustomerNotAuthorizedPopup(
      Model?.FormModel?.VerifyIdentityFormModel?.CustomerNotAuthorizedError,
    );
  }, [Model]);

  const onFocusOutFormInputField = async (e: any, fieldName: any) => {
    formProgression(e, 'Single Payment Logged out', fieldName);
  };

  const handleChange = (e: any, callback: Function) => {
    if (currentMarket === Market.IT) {
      let value = e.target.value.trim();
      const regex = /^[0-9]+$/;
      if (regex.test(value)) {
        setIsFiscalCodeEnabled(true);
        setIsNameEnabled(false);
      } else {
        setIsFiscalCodeEnabled(false);
        setIsNameEnabled(true);
      }

      if (value.length === 0 || value === '') {
        setIsFiscalCodeEnabled(false);
        setIsNameEnabled(false);
      }
    }
    callback(e.target.value);
  };

  function DateOfBirthDesktopUpdate() {
    let day: number = getValues('VerifyIdentityFormModel.DateOfBirthDay');
    let month: number = getValues('VerifyIdentityFormModel.DateOfBirthMonth');
    let year: number = getValues('VerifyIdentityFormModel.DateOfBirthYear');

    if (day > 0 && month >= 0 && year > 0) {
      var dateOfBirth =
        year +
        '-' +
        (month < 10 ? '0' : '') +
        month +
        '-' +
        (day < 10 ? '0' : '') +
        day;
      setValue('VerifyIdentityFormModel.DateOfBirth', dateOfBirth);
    }
  }

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);

    if (window.innerWidth <= 768) {
      DateOfBirthDesktopUpdate();
      return;
    } else {
      DateOfBirthMobileUpdate(null);
      return;
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, [
    width,
    'VerifyIdentityFormModel.DateOfBirth',
    'VerifyIdentityFormModel.DateOfBirthDay',
    'VerifyIdentityFormModel.DateOfBirthMonth',
    'VerifyIdentityFormModel.DateOfBirthYear',
  ]);

  const yearsOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return getNumericOptionsArray(
      1900,
      currentYear,
      PageData?.YearPlaceholder,
      true,
    );
  }, [PageData]);

  const day = (daysOptions.length - 1);
  const month = (monthOptions.length - 1);
  const currentYear = new Date().getFullYear();
  const ToolTipGtm = async () => {
    var tagManagerArgs = {
      dataLayer: {
        event: 'tooltip_open',
        checkout_name: 'Single Payment - Logged Out',
        tooltip_name: 'Where can I find reference number',
      },
    };
    TagManager.dataLayer(tagManagerArgs);
  };

  const handleClickOutside = () => {
    setShowDobValidation(true);
  };
  const DobRef = useRef(null);

  useOnClickOutside(DobRef, handleClickOutside);

  const onSubmit = handleSubmit(async (data: any) => {
    if (
      currentMarket === Market.PL ||
      currentMarket === Market.ES ||
      currentMarket === Market.IT
    ) {
      let {
        DateOfBirthDay,
        DateOfBirthMonth,
        DateOfBirthYear,
        ...newPayload1
      } = data['VerifyIdentityFormModel'];

      data['VerifyIdentityFormModel'] = newPayload1;
      if (currentMarket === Market.IT) {
        data['VerifyIdentityFormModel']['IsFiscalCodeEnabled'] =
          isFiscalCodeEnabled;
        data['VerifyIdentityFormModel']['IsNameEnabled'] = isNameEnabled;
      }
    }
    data['VerifyIdentityFormModel']['IsDisableRecaptcha'] =
      Model?.FormModel?.VerifyIdentityFormModel?.IsDisableRecaptcha;
    if (Model?.FormModel?.VerifyIdentityFormModel?.IsDisableRecaptcha) {
      onVerifyIdentity(data);
    } else {
      if (!executeRecaptcha) {
        return;
      }
      const token = await executeRecaptcha();

      if (token) {
        // Add recaptcha response to VerifyIdentityFormModel
        data.VerifyIdentityFormModel = {
          ...data.VerifyIdentityFormModel,
          RecaptchaResponseToken: token,
        };
        onVerifyIdentity(data);
      } else {
        onVerifyIdentity(data);
      }
    }
  });
  const validateDOB = () => {
    if (watchAllFields) {
      let payload =
        watchAllFields.VerifyIdentityFormModel;

      setHasDOBError(false);
      setDateOfBirthHasError(false);
      if (validateDOBPayload(payload, day, month, currentYear)) {
        setHasDOBError(true);
      }
    }
  };
  const handleChangeDate = (e: any, callback: Function) => {
    if (e.target.value.length >= 2 || e.target.value == 'e') {
      if (
        e.target.value <= day &&
        e.target.value !== '00' &&
        e.target.value !== '000'
      ) {
        e.target.parentNode.nextSibling.firstElementChild.focus();
      }
      e.target.value = e.target.value.slice(0, 2);
    }
    callback(e);
  };
  const handleChangeMonth = (e: any, callback: Function) => {
    if (e.target.value) {
      if (e.target.value.length >= 2 || e.target.value == 'e') {
        if (
          e.target.value <= month &&
          e.target.value !== '00' &&
          e.target.value !== '000'
        ) {
          e.target.parentNode.nextSibling.firstElementChild.focus();
        }
      }

      e.target.value = e.target.value.slice(0, 2);
    }
    callback(e);
  };
  const handleChangeYear = (e: any, callback: Function) => {
    if (e.target.value) {
      if (
        e.target.value.length >= 4 &&
        e.target.value == 'e' &&
        e.target.value !== '00' &&
        e.target.value !== '000'
      ) {
        e.target.value = e.target.value.slice(0, 4);
      }
    }
    callback(e);
  };


  return (
    <div className={styles.container}>
      <form onSubmit={onSubmit} className={styles.wrapper}>
        <div className={styles.debtReference}>
          <Controller
            control={control}
            name={'VerifyIdentityFormModel.DebtReference'}
            render={({
              field: { onChange, name, value },
              fieldState: { isDirty },
            }) => {
              if (isDirty) {
                setShowHiddenInput(true);
              }
              return (
                <FormInput
                  name={name}
                  value={value}
                  label={PageData?.DebtReferenceLabel}
                  tooltipMessage={PageData?.ReferenceTooltipText}
                  onChange={(e) => handleChange(e, onChange)}
                  onBlur={(e) => onFocusOutFormInputField(e, 'DebtReference')}
                  className={classNames(
                    'verifyinput',
                    styles.input,
                    Model?.FormModel?.VerifyIdentityFormModel
                      ?.DebtReferenceHasError
                      ? styles.inputValidationError
                      : styles.input,
                  )}
                />
              );
            }}
          />

          {Model?.FormModel?.VerifyIdentityFormModel?.DebtReferenceHasError && (
            <div className="form-text text-danger">
              {PageData?.DebtReferenceErrorMessage}
            </div>
          )}
          {(currentMarket === Market.FR || currentMarket === Market.DE) &&
            PageData?.ReferenceTooltipTitle && (
              <div>
                <button
                  type="button"
                  className={classNames(
                    styles.explication,
                    'reference-message info_icon w-100',
                  )}
                  onClick={() => {
                    setShowPopup(true);
                    ToolTipGtm();
                  }}
                >
                  {PageData?.ReferenceTooltipTitle}
                </button>
                {showPopup === true && (
                  <div
                    className={classNames(
                      showPopup ? 'modal-overlay is-active' : 'modal-overlay',
                    )}
                  >
                    <div
                      className={classNames(
                        showPopup ? 'modal-dialog is-active' : 'modal-dialog',
                      )}
                    >
                      <ReferenceNumberPopup
                        Model={Model}
                        PageData={PageData}
                        handleClick={setShowPopup}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          {(currentMarket === Market.BE ||
            currentMarket === Market.IT ||
            currentMarket === Market.NL ||
            currentMarket === Market.PL ||
            currentMarket === Market.ES) && (
              <>
                {PageData?.ReferenceTooltipTitle &&
                  PageData?.ReferenceTooltipText && (
                    <>
                      <div
                        className={styles.explication}
                        data-tip
                        data-for="explicationTooltip"
                        onClick={() => ToolTipGtm()}
                      >
                        {PageData?.ReferenceTooltipTitle}
                      </div>
                      <ReactTooltip
                        id="explicationTooltip"
                        place="bottom"
                        effect="solid"
                        multiline={true}
                        className={classNames(styles.tooltip, 'infoicon')}
                      >
                        {PageData?.ReferenceTooltipText}
                      </ReactTooltip>
                    </>
                  )}
              </>
            )}
        </div>
        {!Model?.IsUserAuthenticated && (
          <div className={styles.countryContent}>
            {(currentMarket === Market.FR ||
              currentMarket === Market.DE ||
              currentMarket === Market.NL ||
              currentMarket === Market.BE) && (
                <div className={styles.fieldsWrapper}>
                  <label className={styles.label}>
                    {PageData?.DateOfBirthLabel}
                  </label>
                  <div className={styles.selects} ref={DobRef}>
                    <Controller
                      control={control}
                      name={'VerifyIdentityFormModel.DateOfBirthDay'}
                      render={({ field: { onChange, name, value },
                        fieldState: { isDirty, isTouched },
                      }) => {
                        if (isDirty) {
                          validateDOB();
                        }
                        return (
                          <RegisterFormInput
                            type="number"
                            name={name}
                            value={value}
                            label={''}
                            placeholder={PageData?.DayPlaceholder}
                            onChange={(e) => handleChangeDate(e, onChange)}
                            onBlur={(e) =>
                              onFocusOutFormInputField(e, 'DateOfBirthDay')
                            }
                            className={classNames('selectDOBinput', value != null)}
                            max={day.toString()}
                            onKeyPress={(e) => RegexNumber(e)}
                          />
                        );
                      }}
                    />
                    <Controller
                      control={control}
                      name={'VerifyIdentityFormModel.DateOfBirthMonth'}
                      render={({ field: { onChange, name, value },
                        fieldState: { isDirty, isTouched },
                      }) => {
                        if (isDirty) {
                          validateDOB();
                        }
                        return (
                          <RegisterFormInput
                            type="number"
                            name={name}
                            value={value}
                            label={''}
                            placeholder={PageData?.MonthPlaceholder}
                            onChange={(e) => handleChangeMonth(e, onChange)}
                            onBlur={(e) =>
                              onFocusOutFormInputField(e, 'DateOfBirthMonth')
                            }
                            className={classNames('selectDOBinput', value != null)}
                            max={month.toString()}
                            onKeyPress={(e) => RegexNumber(e)}
                          />
                        );
                      }}
                    />
                    <Controller
                      control={control}
                      name={'VerifyIdentityFormModel.DateOfBirthYear'}
                      render={({ field: { onChange, name, value },
                        fieldState: { isDirty, isTouched },
                      }) => {
                        if (isDirty) {
                          validateDOB();
                        }

                        return (
                          <RegisterFormInput
                            type="number"
                            name={name}
                            value={value}
                            label={''}
                            placeholder={PageData?.YearPlaceholder}
                            onChange={(e) => handleChangeYear(e, onChange)}
                            onBlur={(e) =>
                              onFocusOutFormInputField(e, 'DateOfBirthYear')
                            }
                            className={classNames('selectDOBinput', value != null)}
                            max={currentYear.toString()}
                            min={1900}
                            onKeyPress={(e) => RegexNumber(e)}
                          />
                        );
                      }}
                    />
                  </div>
                  <>
                    <div className={styles.mobiledatapicker}>
                      <Controller
                        control={control}
                        name={'VerifyIdentityFormModel.DateOfBirth'}
                        render={({
                          field: { onChange, name, value },
                          fieldState: { isDirty },
                        }) => {
                          return (
                            <>
                              <RegisterFormInput
                                type="date"
                                name={name}
                                value={value}
                                label={PageData?.DateOfBirthLabel}
                                placeholder="DD/MM/YYYY"
                                onChange={(e) =>
                                  handleDobMobileChange(e, onChange)
                                }
                                onBlur={(e) =>
                                  onFocusOutFormInputField(e, 'DateOfBirth')
                                }
                                max={format(new Date(Date.now()), 'yyyy-MM-dd')}
                                pattern="[0-9]*"
                                className={classNames(styles.widthfull)}
                              />
                            </>
                          );
                        }}
                      />
                    </div>
                  </>
                  {(dateOfBirthHasError || (showDobValidation == true && hasDOBError == true)) && (
                    <div className="form-text text-danger">
                      {PageData?.DateOfBirthErrorMessage}
                    </div>
                  )}
                </div>
              )}
            {(currentMarket === Market.PL || currentMarket === Market.ES) && (
              <div>
                <Controller
                  control={control}
                  name={'VerifyIdentityFormModel.PersonalIdentifier'}
                  render={({ field: { onChange, name, value } }) => (
                    <FormInput
                      name={name}
                      value={value}
                      label={PageData?.PersonalIdentifierLabel}
                      placeholder={PageData?.PersonalIdentifierPlaceholderText}
                      onChange={onChange}
                      onBlur={(e) =>
                        onFocusOutFormInputField(e, 'PersonalIdentifier')
                      }
                      className={classNames(
                        'verifyinput',
                        styles.input,
                        Model?.FormModel?.VerifyIdentityFormModel
                          ?.PersonalIdentifierHasError
                          ? styles.inputValidationError
                          : styles.input,
                      )}
                    />
                  )}
                />
                {Model?.FormModel?.VerifyIdentityFormModel
                  ?.PersonalIdentifierHasError && (
                    <div className="form-text text-danger">
                      {PageData?.PersonalIdentifierErrorMessage}
                    </div>
                  )}
              </div>
            )}
            {currentMarket === Market.IT && (
              <>
                <input
                  type="hidden"
                  {...register('VerifyIdentityFormModel.IsFiscalCodeEnabled')} // Register using name
                  value={Model?.VerifyIdentityFormModel?.IsFiscalCodeEnabled}
                />
                <input
                  type="hidden"
                  {...register('VerifyIdentityFormModel.IsNameEnabled')} // Register using name
                  value={Model?.VerifyIdentityFormModel?.IsNameEnabled}
                />
                <input
                  type="hidden"
                  {...register('VerifyIdentityFormModel.FiscalCode')} // Register using name
                  value={Model?.VerifyIdentityFormModel?.FiscalCode}
                />
                <input
                  type="hidden"
                  {...register('VerifyIdentityFormModel.Name')} // Register using name
                  value={Model?.FormModel?.VerifyIdentityFormModel?.Name}
                />
                {isFiscalCodeEnabled && (
                  <div>
                    <Controller
                      control={control}
                      name="VerifyIdentityFormModel.FiscalCode"
                      render={({ field: { onChange, name, value } }) => (
                        <FormInput
                          name={name}
                          value={value}
                          label={PageData?.FiscalCodeLabel}
                          placeholder={PageData?.FiscalCodePlaceholderText}
                          onChange={onChange}
                          onBlur={(e) =>
                            onFocusOutFormInputField(e, 'FiscalCode')
                          }
                          className={classNames(
                            'verifyinput',
                            styles.input,
                            Model.FormModel?.VerifyIdentityFormModel
                              ?.FiscalCodeHasError
                              ? styles.inputValidationError
                              : styles.input,
                          )}
                        />
                      )}
                    />
                    {Model?.FormModel?.VerifyIdentityFormModel
                      ?.FiscalCodeHasError && (
                        <div className="form-text text-danger">
                          {PageData?.FiscalCodeErrorMessage}
                        </div>
                      )}
                  </div>
                )}
                {isNameEnabled && (
                  <div>
                    <Controller
                      control={control}
                      name="VerifyIdentityFormModel.Name"
                      render={({ field: { onChange, name, value } }) => (
                        <FormInput
                          name={name}
                          value={value}
                          label={PageData?.NameLabel}
                          placeholder={PageData?.NamePlaceholderText}
                          onChange={onChange}
                          onBlur={(e) => onFocusOutFormInputField(e, 'Name')}
                          className={classNames(
                            'verifyinput',
                            styles.input,
                            Model?.FormModel?.VerifyIdentityFormModel
                              ?.NameHasError
                              ? styles.inputValidationError
                              : styles.input,
                          )}
                        />
                      )}
                    />
                    {Model?.FormModel?.VerifyIdentityFormModel
                      ?.NameHasError && (
                        <div className="form-text text-danger">
                          {PageData?.NameErrorMessage}
                        </div>
                      )}
                  </div>
                )}
              </>
            )}
            {Model?.FormModel?.VerifyIdentityFormModel?.showRecaptchaError && (
              <div className="form-text text-danger">
                {PageData?.ReCaptchaErrorMessage}
              </div>
            )}
          </div>
        )}
        <div className={styles.policyInfo}>
          <p className="form-text u-marginBz">
            {PageData.ConfirmPolicyText}{' '}
            <a
              href={PageData.DataProtectionNoticePage?.Uri}
              className="form-text-bold"
              target="_blank"
              rel="noopener"
            >
              {PageData.DataProtectionNotice}
            </a>
          </p>
        </div>
        <button
          type="submit"
          className={classNames('Button w-100', styles.submitBtn)}
        >
          {PageData?.VerifyIdentityButtonLabel}
        </button>
      </form>

      <div
        className={classNames(
          'modal-overlay',
          showValidationPopup ? 'is-active' : '',
        )}
      >
        <ValidationMessage
          Model={Model}
          PageData={PageData}
          LayoutModel={LayoutModel}
          handleClick={() => {
            setRegistrationAttemptCount(registrationAttemptCount + 1);
            setShowValidationPopup(false);
          }}
          ShowSecondAttempt={registrationAttemptCount > 1}
          disableWebDeflect={LayoutModel?.DisableWebDeflect}
        />
      </div>
      <div
        className={classNames(
          'modal-overlay',
          showCustomerNotAuthorizedPopup ? 'is-active' : '',
        )}
      >
        <CustomerNotAuthorized
          Model={Model}
          PageData={PageData}
          LayoutModel={LayoutModel}
          onClose={() => setShowCustomerNotAuthorizedPopup(false)}
        />
      </div>
    </div>
  );
};

export default DebtSection;
